import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { createWorkOrderAssignmentPayout } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderPayoutMutations";
import { workOrderAssignmentRecordQuery } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderAssignmentQuery";
import {
  CreateWorkOrderAssignmentPayoutInput,
  Currency,
  TaxType,
} from "@/gql/graphql";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { CurrencyTypeInput } from "@/app/Common/Components/CurrencyTypeInput";
import { DateInput } from "@/lib/Components/Form/Inputs/DateInput";
import { SelectInput } from "@/lib/Components/Form/Inputs/SelectInput";
import { SwitchInput } from "@/lib/Components/Form/Inputs/SwitchInput";
import { GenericFieldArray } from "@/lib/Components/Form/FieldArray/GenericFieldArray";
import { useFormikContext } from "formik";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { CopyToClipboard } from "@/lib/Components/Common/CopyToClipboard";
import { LandmarkIcon } from "lucide-react";
import { useTranslations } from "use-intl";

const lineItemValidation = z.object({
  description: z.string().nullish(),
  tax_type: z.enum(TaxType),
  amount_inc_tax: z.number().min(1, "Amount is required"),
});

const validation = z.object({
  workOrderAssignmentId: z.string(),
  currency: z.enum(Currency),
  transaction_reference: z.string().min(1, "Transaction reference is required"),
  paid_at: z.string().min(1, "Payment date is required"),
  send_email: z.boolean(),
  lineItems: z.array(lineItemValidation).min(1, "Add at least one line item"),
});

export function AddPayoutDialog({
  workOrderAssignmentId,
}: {
  workOrderAssignmentId: string;
}) {
  const { close, resolve } = useDialogState();
  const { mutateAsync } = useGqlMutation(createWorkOrderAssignmentPayout);
  const { data } = useSuspenseGqlQuery(workOrderAssignmentRecordQuery, {
    id: workOrderAssignmentId,
  });

  const assignment = data.workOrderAssignment;
  const proDriver = assignment.proDriver;
  const currency = assignment.workOrder.currency;

  return (
    <BaseDialog
      title="Add Payout"
      subtitle="Record a payment made to the driver for this assignment"
      size="3xl"
    >
      <div className="mb-6">
        <BankDetailsCard
          bankName={proDriver.bank_name}
          accountName={proDriver.bank_account_name}
          accountNumber={proDriver.bank_account_number}
          identifierCode={proDriver.bank_identifier_code}
        />
      </div>

      <GenericForm<CreateWorkOrderAssignmentPayoutInput>
        initialValues={{
          workOrderAssignmentId,
          currency,
          transaction_reference: null,
          paid_at: new Date().toISOString().split("T")[0],
          send_email: true,
          lineItems: [
            {
              description: "",
              tax_type: TaxType.Reimbursement,
              amount_inc_tax: null!,
            },
          ],
        }}
        validationSchema={toFormikValidationSchema(validation)}
        onSubmit={async (values) => {
          await mutateAsync({ input: values });

          toast.success("Payout recorded successfully");
          await close();
          resolve?.({});
        }}
      >
        <PayoutFormFields />
        <DialogSubmitButton text="Add Payout" />
      </GenericForm>
    </BaseDialog>
  );
}

function BankDetailsCard({
  bankName,
  accountName,
  accountNumber,
  identifierCode,
}: {
  bankName?: string | null;
  accountName?: string | null;
  accountNumber?: string | null;
  identifierCode?: string | null;
}) {
  return (
    <div>
      <h3 className="mb-3 text-sm font-medium text-zinc-700">Bank Details</h3>
      <div className="rounded-lg border border-zinc-200 bg-white p-4">
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-50">
            <LandmarkIcon className="size-5 text-blue-600" />
          </div>
          <div className="grid flex-1 grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-zinc-500">Bank Name</span>
              <CopyToClipboard text={bankName ?? null}>
                <div className="font-medium">{bankName || "--"}</div>
              </CopyToClipboard>
            </div>
            <div>
              <span className="text-zinc-500">Account Name</span>
              <CopyToClipboard text={accountName ?? null}>
                <div className="font-medium">{accountName || "--"}</div>
              </CopyToClipboard>
            </div>
            <div>
              <span className="text-zinc-500">Account Number</span>
              <CopyToClipboard text={accountNumber ?? null}>
                <div className="font-medium">{accountNumber || "--"}</div>
              </CopyToClipboard>
            </div>
            <div>
              <span className="text-zinc-500">BSB / Sort Code</span>
              <CopyToClipboard text={identifierCode ?? null}>
                <div className="font-medium">{identifierCode || "--"}</div>
              </CopyToClipboard>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PayoutFormFields() {
  const { values } = useFormikContext<CreateWorkOrderAssignmentPayoutInput>();
  const t = useTranslations("common.tax_type");

  const taxTypeOptions = [
    { value: TaxType.Gst, label: t(TaxType.Gst) },
    { value: TaxType.NoGst, label: t(TaxType.NoGst) },
    { value: TaxType.Reimbursement, label: t(TaxType.Reimbursement) },
  ];

  return (
    <>
      <CurrencyTypeInput
        name="currency"
        label="Currency"
        className="col-span-3"
      />
      <DateInput name="paid_at" label="Payment Date" className="col-span-4" />
      <TextInput
        name="transaction_reference"
        label="Transaction Reference"
        className="col-span-5"
        autoFocus
      />
      <SwitchInput
        name="send_email"
        label="Email driver"
        description="Send the driver an email notification with the payout invoice"
        className="col-span-12"
      />

      <GenericFieldArray
        label="Line Items"
        fieldArrayKey="lineItems"
        lineItemLabel="line"
        newLineItemValues={{
          description: "",
          tax_type: TaxType.Reimbursement,
          amount_inc_tax: null!,
        }}
        footer={<LineItemsFooter />}
      >
        {(_item, index) => (
          <>
            <SelectInput
              name={`lineItems.${index}.tax_type`}
              label="Tax"
              className="col-span-4"
              options={[{ label: "Tax type", options: taxTypeOptions }]}
            />
            <CurrencyInput
              name={`lineItems.${index}.amount_inc_tax`}
              label="Amount (inc tax)"
              className="col-span-8"
              currency={values.currency}
            />

            <TextInput
              name={`lineItems.${index}.description`}
              label="Description"
              className="col-span-12"
            />
          </>
        )}
      </GenericFieldArray>
    </>
  );
}

function LineItemsFooter() {
  const { values } = useFormikContext<CreateWorkOrderAssignmentPayoutInput>();

  const totalIncTax = values.lineItems.reduce(
    (sum, line) => sum + (line.amount_inc_tax ?? 0),
    0,
  );

  const totalTax = values.lineItems.reduce((sum, line) => {
    const amount = line.amount_inc_tax ?? 0;
    if (line.tax_type === TaxType.Gst) {
      return sum + Math.round(amount - amount / 1.1);
    }
    return sum;
  }, 0);

  return (
    <div className="space-y-1 text-sm">
      <div className="flex justify-end gap-8">
        <span className="text-gray-500">Subtotal (exc. tax)</span>
        <span className="w-28 text-right font-medium">
          {formatCurrency(totalIncTax - totalTax, values.currency)}
        </span>
      </div>
      <div className="flex justify-end gap-8">
        <span className="text-gray-500">Tax</span>
        <span className="w-28 text-right font-medium">
          {formatCurrency(totalTax, values.currency)}
        </span>
      </div>
      <div className="flex justify-end gap-8 border-t border-gray-200 pt-1 font-semibold">
        <span>Total</span>
        <span className="w-28 text-right">
          {formatCurrency(totalIncTax, values.currency)}
        </span>
      </div>
    </div>
  );
}
